/** /notification_materials/ — durable design-import inputs (Figma plan §3.6).
 *
 * A Figma link is a material, an uploaded/pasted HTML file is a material.
 * Import tasks consume materials, never transient uploads — they are
 * re-readable across sessions, re-prioritized, deprecated and superseded when
 * a client sends a v2 brochure. No delete endpoint: materials are provenance;
 * `deprecated` is the lifecycle end-state.
 *
 * Upload staging follows the ai_import discipline: `upload()` stages ONE
 * UTF-8 text (HTML) file to S3 and returns a marker-guarded `file_key`;
 * `create()` accepts that key (or pasted `html`) and the backend fills
 * `extracted.html` — exactly what ingest consumes.
 *
 * Hand-authored wrapper (console convention: never depend on generated types
 * for feature modules).
 */
import { apiGet, apiPatch, apiPost, client } from './client';
import type { PaginatedResponse } from './users';
import type { TenantIdsParam } from '@/scope/tenantIds';
import type { TourOperatorEnvironment } from './tourOperators';

export interface UserBrief {
  id: number;
  email: string;
  name: string;
}

export interface MaterialTenant {
  id: number;
  name: string;
  code: string | null;
  /** `Tour_Operator.kind` — the Tenant cell marks a sandbox (plan D8). */
  environment?: TourOperatorEnvironment | null;
}

/** One proposed event per template frame from a Figma discover run. */
export interface FigmaFrameProposal {
  frame_id: string;
  frame_name: string;
  event_code: string | null;
  confidence: number;
  reason: string;
}

export interface MaterialFigmaInfo {
  file_key: string;
  node_id: string | null;
  version: string;
  frames: FigmaFrameProposal[];
}

export type MaterialKind =
  | 'figma'
  | 'html'
  | 'pdf'
  | 'docx'
  | 'txt'
  | 'image'
  | 'chat_export'
  | 'email_thread'
  | 'url'
  | 'note';

export interface MaterialListItem {
  id: number;
  tour_operator: MaterialTenant;
  kind: MaterialKind;
  title: string;
  description: string;
  url: string;
  file_key: string;
  content_hash: string;
  /** Higher wins — "document X is more relevant than document Y". */
  priority: number;
  deprecated: boolean;
  deprecated_ts: string | null;
  deprecated_by: UserBrief | null;
  /** Id of the material this one replaces (client sent a v2). */
  supersedes: number | null;
  has_html: boolean;
  /** Discover-run cache; null until a Figma discover task has run. */
  figma: MaterialFigmaInfo | null;
  proposal_count: number | null;
  created_by: UserBrief | null;
  created_ts: string | null;
  updated_ts: string | null;
}

export interface MaterialProposalBrief {
  id: number;
  status: string;
  origin: string;
  created_ts: string;
}

export interface MaterialDetail extends MaterialListItem {
  /** The extracted HTML source (html materials). */
  html: string | null;
  proposals: MaterialProposalBrief[];
}

export interface MaterialCreate {
  tour_operator: number;
  kind: MaterialKind;
  title: string;
  description?: string;
  /** Required for kind=figma (validated figma.com design/file URL). */
  url?: string;
  /** Pasted HTML source (kind=html; alternative to file_key). */
  html?: string;
  /** Marker-guarded S3 key from upload() (kind=html). */
  file_key?: string;
  priority?: number;
  supersedes?: number;
}

export interface MaterialUpdate {
  title?: string;
  description?: string;
  priority?: number;
  deprecated?: boolean;
  supersedes?: number | null;
}

export interface UploadedMaterialFile {
  file_key: string;
  filename: string;
  size: number;
}

export interface ListParams {
  page?: number;
  pageSize?: number;
  ordering?: string;
  /** title + description + url (icontains). */
  search?: string;
  /** One id or a comma list of ids (D1). */
  tour_operator_id?: TenantIdsParam;
  kind?: MaterialKind;
  deprecated?: boolean;
}

function toQuery(params: ListParams): string {
  const usp = new URLSearchParams();
  if (params.page) usp.set('page', String(params.page));
  if (params.pageSize) usp.set('page_size', String(params.pageSize));
  if (params.ordering) usp.set('ordering', params.ordering);
  if (params.search) usp.set('search', params.search);
  if (params.tour_operator_id !== undefined) {
    usp.set('tour_operator_id', String(params.tour_operator_id));
  }
  if (params.kind) usp.set('kind', params.kind);
  if (params.deprecated !== undefined) usp.set('deprecated', String(params.deprecated));
  const qs = usp.toString();
  return qs ? `?${qs}` : '';
}

export function list(params: ListParams = {}): Promise<PaginatedResponse<MaterialListItem>> {
  return apiGet<PaginatedResponse<MaterialListItem>>(`/notification_materials/${toQuery(params)}`);
}

export function retrieve(id: number): Promise<MaterialDetail> {
  return apiGet<MaterialDetail>(`/notification_materials/${id}/`);
}

export function create(body: MaterialCreate): Promise<MaterialListItem> {
  return apiPost<MaterialListItem>('/notification_materials/', body);
}

export function update(id: number, body: MaterialUpdate): Promise<MaterialListItem> {
  return apiPatch<MaterialListItem>(`/notification_materials/${id}/`, body);
}

/** Stage one HTML file to S3; the returned file_key goes into create(). */
export async function upload(file: File): Promise<UploadedMaterialFile> {
  const fd = new FormData();
  fd.append('file', file);
  // Let axios set the multipart boundary itself.
  const { data } = await client.post<UploadedMaterialFile>('/notification_materials/upload/', fd, {
    headers: { 'Content-Type': undefined },
  });
  return data;
}
